import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { backofficeApi } from '@/lib/backoffice-api'
import { setStaffAccessToken } from '@/lib/backoffice-auth'

interface LoginForm {
  email: string
  password: string
}

export function BackofficeLoginPage() {
  const navigate = useNavigate()
  const [apiError, setApiError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>()

  async function onSubmit(data: LoginForm) {
    setApiError(null)
    try {
      const res = await backofficeApi.post<{ accessToken: string }>('/backoffice/auth/login', data)
      setStaffAccessToken(res.data.accessToken)
      navigate('/backoffice', { replace: true })
    } catch (err) {
      const msg = isAxiosError(err) ? (err.response?.data?.error?.message as string | undefined) : undefined
      setApiError(msg ?? 'Erro ao fazer login.')
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            TransportPro · Backoffice
          </h1>
          <p className="text-sm text-muted-foreground">Console da plataforma</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="operador@transportpro.com"
              aria-invalid={!!errors.email}
              {...register('email', { required: 'Informe seu email' })}
            />
            {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              aria-invalid={!!errors.password}
              {...register('password', { required: 'Informe sua senha' })}
            />
            {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
          </div>

          {apiError && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {apiError}
            </p>
          )}

          <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
            {isSubmitting ? 'Entrando…' : 'Entrar'}
          </Button>
        </form>
      </div>
    </div>
  )
}
